import { constant, noop } from 'lodash';
import { useCallback, useMemo } from 'react';

import { type PublicClip } from '@air/api';
import { type CardSize, type Clip } from '@air/api/types';
import { type GallerySection } from '@air/component-gallery-view';
import { useBreakpointsContext } from '@air/provider-media-query';
import { type Upload, UploadStatus } from '@air/redux-uploader';
import { ClipUtils } from '@air/utils-clips';

import { type GalleryMetadata } from '~/components/Gallery/galleryMetadata';
import { GalleryItemType, type GalleryViewRender } from '~/components/Gallery/types';
import { UploadingAssetGalleryCard } from '~/components/Gallery/UploadingAssetGalleryCard';
import { useGallerySectionFooter } from '~/components/Gallery/useGallerySectionFooter';
import {
  type UseGallerySectionHeaderParams,
  useGallerySectionHeader,
} from '~/components/Gallery/useGallerySectionHeader';
import { DragType } from '~/components/Shared/Drag/dragTypes';
import { useJustifiedGrid } from '~/components/Shared/JustifiedGrid';
import { WKSPC_DESKTOP_HORIZONTAL_PADDING_WITH_SIDENAV_OPEN } from '~/constants/WorkspaceSpacing';
import { getClipRatio } from '~/hooks/useClipRatios';
import { type UseGalleryDataInfo } from '~/swr-hooks/gallery/types';
import { getUploadDimensions } from '~/utils/uploadDimensionsCache';

const VERTICAL_SPACING_SMALL = 12;
const VERTICAL_SPACING_DEFAULT = 24;

export const AssetGalleryCardSize: { [key in CardSize]: number } = {
  small: 165,
  medium: 220,
  large: 440,
  'extra-large': 660,
};

export type UseGalleryAssetsParams<C> = Pick<UseGallerySectionHeaderParams, 'renderHeader'> & {
  data?: UseGalleryDataInfo<C>;
  containerWidth: number;
  renderAsset: ((props: GalleryViewRender<C>) => JSX.Element) | undefined;
  loadMore?: () => void;
  uploads?: Upload[];
  isFirstSection: boolean;
  containerHorizontalPadding?: number;
  headerContainerHeight?: number;
  itemAdditionalHeight?: number;
  itemHeight: number;
  itemHorizontalSpacing?: number;
  isSelectable?: (clip: C) => boolean;
  onSectionCollapse?: (isCollapsed: boolean) => void;
  rowVerticalSpacing?: number;
};

export const useGalleryAssets = <C extends Clip | PublicClip>({
  renderAsset,
  containerWidth,
  loadMore = noop,
  data,
  uploads = [],
  itemHeight,
  itemHorizontalSpacing,
  isFirstSection,
  itemAdditionalHeight = 0,
  headerContainerHeight,
  onSectionCollapse,
  containerHorizontalPadding = WKSPC_DESKTOP_HORIZONTAL_PADDING_WITH_SIDENAV_OPEN,
  isSelectable = constant(false),
  renderHeader,
  rowVerticalSpacing,
}: UseGalleryAssetsParams<C>): GallerySection<GalleryMetadata> | null => {
  const { isAboveSmallScreen } = useBreakpointsContext();

  const filteredUploads = useMemo(() => {
    const existingClipIds = new Set((data?.items ?? []).map((c) => c.id));
    const finishedClipIds = new Set(
      (data?.items ?? []).filter((c) => ClipUtils.isTranscodeFinished(c.status)).map((c) => c.id),
    );
    // Hide upload card as soon as spriteSheet is available so videos become scrubbable
    // without waiting for full transcode completion
    const scrubReadyClipIds = new Set(
      (data?.items ?? [])
        .filter(
          (c): c is C & { assets: { spriteSheet: { src: string } } } =>
            'spriteSheet' in c.assets && !!c.assets.spriteSheet?.src,
        )
        .map((c) => c.id),
    );

    return uploads.filter((u) => {
      if (finishedClipIds.has(u.clipId) || scrubReadyClipIds.has(u.clipId)) {
        return false;
      }
      // Upload is done but clip is gone (deleted) — don't show zombie upload card
      if (u.status === UploadStatus.completed && !existingClipIds.has(u.clipId)) {
        return false;
      }

      return true;
    });
  }, [data?.items, uploads]);

  const filteredClips = useMemo(() => {
    const activeClipIds = new Set(filteredUploads.map((u) => u.clipId));

    return (data?.items ?? []).filter((c) => !activeClipIds.has(c.id));
  }, [data?.items, filteredUploads]);

  const uploadDimsByClipId = useMemo(() => {
    const map = new Map<string, { width: number; height: number }>();
    for (const upload of uploads) {
      if (upload.clipId && upload.width && upload.height) {
        map.set(upload.clipId, { width: upload.width, height: upload.height });
      }
    }
    return map;
  }, [uploads]);

  type AssetEntry =
    | { type: typeof GalleryItemType.upload; item: Upload }
    | { type: typeof GalleryItemType.asset; item: C };

  const assets = useMemo(
    (): AssetEntry[] => [
      ...filteredUploads.map(
        (u): AssetEntry => ({
          item: u,
          type: GalleryItemType.upload,
        }),
      ),
      ...filteredClips.map(
        (u): AssetEntry => ({
          item: u,
          type: GalleryItemType.asset,
        }),
      ),
    ],
    [filteredClips, filteredUploads],
  );

  const hiddenClipsCount = (data?.items?.length ?? 0) - filteredClips.length;
  const total = (data?.total ?? 0) - hiddenClipsCount + filteredUploads.length;

  const ratios = [
    ...filteredUploads.map(({ apiUploadData, width, height }) =>
      getClipRatio({ width: width ?? 0, height: height ?? 0, type: apiUploadData.type, ext: apiUploadData.ext }),
    ),
    ...filteredClips.map((clip) => {
      if (!clip.width || !clip.height) {
        // Prefer clipId-keyed lookup to handle same-named concurrent uploads.
        // Fall back to importedName for canvas imports (UUID-based importedName, always unique).
        const cached = uploadDimsByClipId.get(clip.id) ?? getUploadDimensions(clip.importedName);

        if (cached) {
          return getClipRatio({ ...clip, ...cached });
        }
      }

      return getClipRatio(clip);
    }),
  ];

  const { boxes: assetBoxes } = useJustifiedGrid({
    containerWidth: containerWidth,
    ratios,
    options: {
      containerPadding: {
        left: containerHorizontalPadding,
        right: containerHorizontalPadding,
      },
      targetRowHeight: itemHeight,
      boxSpacing: {
        horizontal: itemHorizontalSpacing ?? 24,
      },
      additionalRowHeight: itemAdditionalHeight,
    },
  });

  const renderItem = useCallback(
    (index: number) => {
      const asset = assets[index];

      if (asset) {
        return asset.type === GalleryItemType.upload ? (
          <UploadingAssetGalleryCard
            key={asset.item.id}
            width={
              /**
               * Uploads don't inherently have a width and get it from the ratio of the
               * row height
               */
              itemHeight
            }
            upload={asset.item}
          />
        ) : (
          renderAsset?.({
            box: assetBoxes[index],
            data: asset.item,
            index,
          })
        );
      }

      return null;
    },
    [assets, itemHeight, renderAsset, assetBoxes],
  );

  const verticalSpacing = !isAboveSmallScreen ? VERTICAL_SPACING_SMALL : VERTICAL_SPACING_DEFAULT;

  const getItemData = useCallback(
    (index: number): GalleryMetadata => ({
      type: assets[index].type,
      isSelectable: assets[index].type === GalleryItemType.asset ? isSelectable(assets[index].item) : false,
      itemId: assets[index].item.id,
      canDragTo: (item) => item.type === DragType.asset,
    }),
    [assets, isSelectable],
  );

  const { isHeaderCollapsed, header } = useGallerySectionHeader({
    isFirstSection,
    label: 'Assets',
    sectionType: GalleryItemType.asset,
    itemCount: total,
    containerWidth,
    containerPadding: containerHorizontalPadding,
    containerHeight: headerContainerHeight,
    onSectionCollapse,
    renderHeader,
  });

  const footer = useGallerySectionFooter({
    isLoading: !!data?.isLoading,
    loadMore,
    hasMore: !!data?.hasMore,
    containerWidth,
  });

  if (!total && !data?.isLoading) {
    return null;
  }

  return {
    header: total ? header : undefined,
    footer: isHeaderCollapsed ? undefined : footer,
    items: {
      render: renderItem,
      boxes: isHeaderCollapsed ? [] : assetBoxes,
      verticalItemsSpacing: rowVerticalSpacing ?? verticalSpacing,
      getData: getItemData,
    },
  };
};
